import { doc, updateDoc, increment, getDoc, arrayUnion, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import API_CONFIG from "../config/api"

// Define point values for different actions
export const POINTS_VALUES = {
  POST_QUERY: 5,
  UPVOTE_RECEIVED: 2,
  COMMENT_POSTED: 2,
  REPLY_POSTED: 1,
  BEST_ANSWER: 10,
  DAILY_LOGIN: 1,
  PROFILE_COMPLETION: 5,
  CHALLENGE_COMPLETED: 0, // This is just a placeholder, actual values set in challenges
};

// Define user levels based on point ranges
export const USER_LEVELS = [
  { name: "Newbie", minPoints: 0, maxPoints: 49, badge: "🌱" },
  { name: "Explorer", minPoints: 50, maxPoints: 149, badge: "🔍" },
  { name: "Contributor", minPoints: 150, maxPoints: 399, badge: "🌟" },
  { name: "Innovator", minPoints: 400, maxPoints: 799, badge: "💡" },
  { name: "Expert", minPoints: 800, maxPoints: 1499, badge: "🏆" },
  { name: "Master", minPoints: 1500, maxPoints: 2999, badge: "👑" },
  { name: "Legend", minPoints: 3000, maxPoints: Infinity, badge: "🔮" }
];

// Define available rewards
export const REWARDS = [
  {
    id: "ai_features_unlock",
    name: "AI Assistant Features",
    description: "Unlock all AI-powered features for your account for 2 months",
    type: "feature",
    cost: 15000,
    duration: 60 // 60 days (2 months)
  },
  {
    id: "badge_pack_1",
    name: "Badge Pack I",
    description: "Unlock a set of special badges to display on your profile",
    type: "badge",
    cost: 100,
    options: ["🚀", "🦉", "🌈", "🦊", "🐉"]
  },
  {
    id: "badge_pack_2",
    name: "Badge Pack II",
    description: "Unlock premium badges for your profile",
    type: "badge",
    cost: 250,
    options: ["⭐", "🔱", "⚡", "🌌", "🌠"]
  },
  {
    id: "query_boost_small",
    name: "Small Query Boost",
    description: "Boost a query's visibility for 2 days",
    type: "boost",
    cost: 50,
    duration: 2,
    expires: true,
    boostLevel: "regular"
  },
  {
    id: "query_boost_medium",
    name: "Medium Query Boost",
    description: "Boost a query's visibility for 5 days and feature in trending",
    type: "boost",
    cost: 100,
    duration: 5,
    expires: true,
    boostLevel: "trending"
  },
  {
    id: "query_boost_large",
    name: "Large Query Boost",
    description: "Boost a query's visibility for 10 days and feature at top of browse",
    type: "boost",
    cost: 200,
    duration: 10,
    expires: true,
    boostLevel: "featured"
  }
];

// Define challenges
export const CHALLENGES = {
  daily: [
    {
      id: "daily_login",
      name: "Daily Login",
      description: "Log in to the platform once today to earn points",
      points: 2,
      targetProgress: 1,
      progressLabel: "logins",
      type: "login"
    },
    {
      id: "daily_vote",
      name: "Daily Upvotes",
      description: "Upvote 3 helpful posts or comments to improve content quality",
      points: 5,
      targetProgress: 3,
      progressLabel: "upvotes",
      type: "upvote"
    },
    {
      id: "daily_comment",
      name: "Daily Engagement",
      description: "Leave 2 comments on queries to foster community discussion",
      points: 8,
      targetProgress: 2,
      progressLabel: "comments",
      type: "comment"
    }
  ],
  weekly: [
    {
      id: "weekly_query",
      name: "Content Creator",
      description: "Post 2 meaningful queries to contribute to the community knowledge base",
      points: 25,
      targetProgress: 2,
      progressLabel: "queries",
      type: "query"
    },
    {
      id: "weekly_best_answer",
      name: "Quality Contributor",
      description: "Get a comment selected as best answer by having insightful and helpful responses",
      points: 40,
      targetProgress: 1,
      progressLabel: "best answers",
      type: "best_answer"
    },
    {
      id: "weekly_upvotes",
      name: "Community Favorite",
      description: "Receive 10 upvotes on your content by creating valuable posts and comments",
      points: 30,
      targetProgress: 10,
      progressLabel: "upvotes received",
      type: "upvote_received"
    }
  ]
};

// Create a new user with initial points
export async function createUser(userId, username, email, displayName = "", photoURL = null) {
  try {
    const response = await fetch(API_CONFIG.getUrl('createUser'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        username,
        email,
        displayName,
        photoURL
      }),
    });
    return await response.json();
  } catch (error) {
    console.error('Error creating user:', error);
    return { success: false, error: error.message };
  }
}

// Update user points
export async function updatePoints(userId, points) {
  try {
    const response = await fetch(API_CONFIG.getUrl('updatePoints'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        points
      }),
    });
    return await response.json();
  } catch (error) {
    console.error('Error updating points:', error);
    return { success: false, error: error.message };
  }
}

// Fetch user points
export async function fetchUserPoints(userId) {
  try {
    const response = await fetch(API_CONFIG.getUrl(`api/getUserPoints?userId=${userId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (response.ok) {
      const data = await response.json();
      return data.total !== undefined ? data.total : data.points !== undefined ? data.points : 0;
    }
    return 0;
  } catch (error) {
    console.error('Error fetching user points:', error);
    return 0;
  }
}

// Fetch comprehensive user data (points, rewards, badges, etc.)
export async function fetchUserData(userId) {
  try {
    const response = await fetch(API_CONFIG.getUrl(`getUserData?userId=${userId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return data.success ? data.userData : null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
}

// Get user level based on points
export function getUserLevel(points) {
  for (const level of USER_LEVELS) {
    if (points >= level.minPoints && points <= level.maxPoints) {
      return level;
    }
  }
  return USER_LEVELS[0]; // Default to the first level if no match
}

// Redeem a reward
export async function redeemReward(userId, rewardId, rewardData = {}) {
  try {
    // Find the reward in the rewards list
    const reward = REWARDS.find(r => r.id === rewardId);
    
    if (!reward) {
      return { 
        success: false, 
        error: `Reward ${rewardId} not found` 
      };
    }
    
    // Get user data and points
    const userPoints = await fetchUserPoints(userId);
    
    if (userPoints < reward.cost) {
      return { 
        success: false, 
        error: `Not enough points. You need ${reward.cost} points, but have ${userPoints} points.` 
      };
    }
    
    const now = new Date();
    const userRef = doc(db, 'users', userId);
    
    // Process specific reward types
    let specialData = {};
    
    // For theme rewards, save the selected theme to user document
    if (reward.type === 'theme' && rewardData.option) {
      await updateDoc(userRef, { theme: rewardData.option });
      specialData = { appliedTheme: rewardData.option };
    }
    
    // For badge rewards, add individual entries for each badge in the pack
    if (reward.type === 'badge') {
      const badgesToAdd = reward.options.map(emoji => {
        // Create a simple name based on the emoji - this could be improved
        const badgeName = `${emoji} Badge`; 
        return {
          id: `${reward.id}_${emoji}`, // Create a unique ID for each badge
          name: badgeName,
          type: 'badge',
          redeemedAt: now.toISOString(),
          badgeEmoji: emoji, // Store the specific emoji
          packId: reward.id // Keep track of the source pack
        };
      });
      
      // Update user document: Deduct points and add all badges from the pack
      await updateDoc(userRef, {
        points: increment(-reward.cost),
        // Use arrayUnion to add multiple badge objects
        rewards: arrayUnion(...badgesToAdd) 
      });
      
      // Return success with data for the first badge as representative (or could return all)
      return {
        success: true,
        remainingPoints: userPoints - reward.cost,
        rewardData: badgesToAdd[0] // Return first badge data for consistency
      };
    }
    
    // Create reward redemption data
    const redemptionData = {
      id: rewardId,
      name: reward.name,
      type: reward.type,
      redeemedAt: now.toISOString(),
      ...specialData
    };
    
    // Add expiration if the reward expires
    if (reward.expires || reward.duration) {
      const expirationDate = new Date();
      expirationDate.setDate(now.getDate() + (reward.duration || 30)); // Default to 30 days if no duration
      redemptionData.expiresAt = expirationDate.toISOString();
    }
    
    // Update the user document with reduced points and new reward
    await updateDoc(userRef, {
      points: increment(-reward.cost),
      rewards: arrayUnion(redemptionData)
    });
    
    // Special handling for AI features - update the subcollection document
    if (rewardId === 'ai_features_unlock') {
      const expiryDate = new Date();
      expiryDate.setDate(now.getDate() + reward.duration); // Use reward duration
      
      const featureSubDoc = doc(db, 'users', userId, 'features', 'ai_features_unlock');
      await setDoc(featureSubDoc, {
        unlocked: true,
        unlockedAt: now.toISOString(),
        expiryDate: expiryDate.toISOString(),
        pointsSpent: reward.cost,
        purchasedWithPoints: true
      }, { merge: true });
    }
    
    // Return success result with remaining points
    return {
      success: true,
      remainingPoints: userPoints - reward.cost,
      rewardData: redemptionData
    };
  } catch (error) {
    console.error('Error redeeming reward:', error);
    return { 
      success: false, 
      error: error.message 
    };
  }
}

// Apply boost to a query
export async function boostQuery(userId, queryId, boostId) {
  try {
    const boost = REWARDS.find(r => r.id === boostId);
    if (!boost || boost.type !== 'boost') {
      return { success: false, error: 'Invalid boost type' };
    }
    
    const userPoints = await fetchUserPoints(userId);
    if (userPoints < boost.cost) {
      return { 
        success: false, 
        error: `Not enough points. Need ${boost.cost - userPoints} more points.` 
      };
    }
    
    const response = await fetch(API_CONFIG.getUrl('boostQuery'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        userId, 
        queryId, 
        boostId,
        cost: boost.cost,
        duration: boost.duration
      }),
    });

    const data = await response.json();
    
    if (data.success) {
      return { 
        success: true, 
        remainingPoints: data.remainingPoints,
        boostExpiry: data.boostExpiry
      };
    } else {
      return { success: false, error: data.error };
    }
  } catch (error) {
    console.error('Error applying boost:', error);
    return { success: false, error: 'Failed to apply boost.' };
  }
}

// Get user's challenges
export async function fetchUserChallenges(userId) {
  try {
    const response = await fetch(API_CONFIG.getUrl(`getUserChallenges?userId=${userId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const data = await response.json();
    return data.success ? data.challenges : [];
  } catch (error) {
    console.error('Error fetching user challenges:', error);
    return [];
  }
}

// Initialize challenges for a user (typically called on first load or new day/week)
export async function initializeChallenges(userId) {
  try {
    const response = await fetch(API_CONFIG.getUrl('initializeChallenges'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    return await response.json();
  } catch (error) {
    console.error('Error initializing challenges:', error);
    return { success: false, error: error.message };
  }
}

// Update challenge progress
export async function updateChallengeProgress(userId, challengeId, increment, challengePoints) {
  try {
    const response = await fetch(API_CONFIG.getUrl('updateChallengeProgress'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        challengeId,
        increment,
        challengePoints
      }),
    });
    return await response.json();
  } catch (error) {
    console.error('Error updating challenge progress:', error);
    return { success: false, error: error.message };
  }
}

// Add Firebase direct methods for manual points management
// These functions can be used by admins to directly modify user points

// Directly set a user's points in Firebase
export async function setUserPointsDirectly(userId, points) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    // Try to find user by userId first
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // If user is not found by ID, try to find by email
      // This allows admins to enter an email instead of a Firebase UID
      if (userId.includes('@')) {
        console.log('Looking up user by email:', userId);
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          return { success: false, error: 'User not found with the provided email' };
        }
        
        // Use the first user found with this email
        const foundUserDoc = querySnapshot.docs[0];
        userId = foundUserDoc.id;
        console.log('Found user by email, using ID:', userId);
      } else {
        return { success: false, error: 'User not found' };
      }
    }
    
    // Update the user's points
    const actualUserRef = doc(db, 'users', userId);
    await updateDoc(actualUserRef, { points });
    
    // Also update the points in stats collection
    const statsRef = doc(db, 'users', userId, 'stats', 'points');
    await updateDoc(statsRef, { 
      total: points,
      lastUpdated: serverTimestamp()
    });
    
    return { 
      success: true, 
      message: `User points set to ${points}` 
    };
  } catch (error) {
    console.error('Error setting user points:', error);
    return { success: false, error: error.message };
  }
}

// Add points to a user (can be negative to subtract)
export async function addUserPointsDirectly(userId, pointsToAdd) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    // Try to find user by userId first
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // If user is not found by ID, try to find by email
      // This allows admins to enter an email instead of a Firebase UID
      if (userId.includes('@')) {
        console.log('Looking up user by email:', userId);
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          return { success: false, error: 'User not found with the provided email' };
        }
        
        // Use the first user found with this email
        const foundUserDoc = querySnapshot.docs[0];
        userId = foundUserDoc.id;
        console.log('Found user by email, using ID:', userId);
      } else {
        return { success: false, error: 'User not found' };
      }
    }
    
    // Get current points
    const actualUserRef = doc(db, 'users', userId);
    const actualUserDoc = await getDoc(actualUserRef);
    const currentPoints = actualUserDoc.data().points || 0;
    const newTotal = Math.max(0, currentPoints + pointsToAdd);
    
    // Update user points in main document
    await updateDoc(actualUserRef, { 
      points: newTotal
    });
    
    // Also update the points in stats collection
    const statsRef = doc(db, 'users', userId, 'stats', 'points');
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      await updateDoc(statsRef, { 
        total: newTotal,
        lastUpdated: serverTimestamp()
      });
    } else {
      await setDoc(statsRef, {
        total: newTotal,
        lastUpdated: serverTimestamp()
      });
    }
    
    return { 
      success: true, 
      message: `Added ${pointsToAdd} points to user`,
      newTotal: newTotal
    };
  } catch (error) {
    console.error('Error adding user points:', error);
    return { success: false, error: error.message };
  }
}

// Add AI feature to a user
export async function addAIFeature(userId) {
  try {
    if (!userId) {
      throw new Error('User ID is required');
    }
    
    const now = new Date();
    const expiryDate = new Date();
    expiryDate.setDate(now.getDate() + 30); // 30 days free trial
    
    // Create feature data
    const featureData = {
      id: 'ai_features_unlock',
      unlockedAt: now.toISOString(),
      expiryDate: expiryDate.toISOString(),
      trial: true,
      purchasedWithPoints: false
    };
    
    // Update user document
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    // Add to the features array
    await updateDoc(userRef, {
      features: arrayUnion(featureData)
    });
    
    // Also create a document in the features subcollection
    const featureDocRef = doc(db, 'users', userId, 'features', 'ai_features_unlock');
    await setDoc(featureDocRef, {
      unlocked: true,
      unlockedAt: now.toISOString(),
      expiryDate: expiryDate.toISOString(),
      trial: true
    }, { merge: true });
    
    return { 
      success: true,
      expiryDate: expiryDate.toISOString() 
    };
  } catch (error) {
    console.warn('Error adding AI feature:', error);
    return { success: false, error: error.message };
  }
}

// Check if user has valid AI features access
export async function checkAIAccess(userId) {
  try {
    const response = await fetch(API_CONFIG.getUrl(`checkAIAccess?userId=${userId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return await response.json();
  } catch (error) {
    console.error('Error checking AI access:', error);
    return { success: false, error: error.message };
  }
}

// Store AI feature trial usage for a specific query
export async function storeAIFeatureTrialUsage(userId, queryId, feature) {
  try {
    // Create a unique key for this specific feature
    const trialDataKey = `ai_trial_${feature}_${queryId}`;
    
    // Store in localStorage for this browser
    const existingStoredData = localStorage.getItem(trialDataKey);
    if (existingStoredData) {
      return { success: true, alreadyUsed: true };
    }
    
    // Store basic information about trial usage
    const trialData = {
      userId,
      queryId,
      feature,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(trialDataKey, JSON.stringify(trialData));
    
    // Also add a general record of AI trials
    try {
      const allTrials = JSON.parse(localStorage.getItem('ai_trial_usages') || '[]');
      allTrials.push(trialData);
      localStorage.setItem('ai_trial_usages', JSON.stringify(allTrials));
    } catch (e) {
      console.warn('Error storing global trial usage data:', e);
    }
    
    return { success: true };
  } catch (error) {
    console.warn('Error storing trial usage:', error);
    return { success: false, error: error.message };
  }
}

// Check if user has already used a trial for a specific query
export function checkAIFeatureTrialUsage(userId, queryId, feature) {
  try {
    // Create a unique key for this specific feature
    const trialDataKey = `ai_trial_${feature}_${queryId}`;
    
    // Check localStorage for this browser
    const existingStoredData = localStorage.getItem(trialDataKey);
    if (existingStoredData) {
      return { trialUsed: true };
    }
    
    return { trialUsed: false };
  } catch (error) {
    console.warn('Error checking trial usage:', error);
    return { trialUsed: false, error: error.message };
  }
}

// Helper function to find user by email
export async function findUserByEmail(email) {
  try {
    if (!email || !email.includes('@')) {
      return null;
    }
    
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const userDoc = querySnapshot.docs[0];
    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    console.warn('Error finding user by email:', error);
    return null;
  }
}


// Directly grant a feature to a user (admin function)
export async function grantFeatureToUser(userId, featureId, durationDays = null) {
  try {
    if (!userId || !featureId) {
      throw new Error('User ID and feature ID are required');
    }
    
    // Try to find user by userId first
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // If user is not found by ID, try to find by email
      // This allows admins to enter an email instead of a Firebase UID
      if (userId.includes('@')) {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          return { success: false, error: 'User not found with the provided email' };
        }
        
        // Use the first user found with this email
        const foundUserDoc = querySnapshot.docs[0];
        userId = foundUserDoc.id;
      } else {
        return { success: false, error: 'User not found' };
      }
    }
    
    // Calculate expiry date
    const now = new Date();
    const expiryDate = durationDays 
      ? new Date(now.getTime() + (durationDays * 24 * 60 * 60 * 1000)) 
      : null;
    
    // Create the feature data - note the addition of adminGranted flag
    const featureData = {
      id: featureId,
      grantedAt: now.toISOString(),
      expiryDate: expiryDate ? expiryDate.toISOString() : null,
      adminGranted: true,
      purchasedWithPoints: false // This ensures we can distinguish from rewards purchases
    };
    
    // Re-fetch user document if we found it by email
    const actualUserRef = doc(db, 'users', userId);
    const actualUserDoc = await getDoc(actualUserRef);
    const userData = actualUserDoc.data();
    const features = userData.features || [];
    
    // Check if feature already exists, update it if so
    const existingFeatureIndex = features.findIndex(f => f.id === featureId);
    if (existingFeatureIndex >= 0) {
      features[existingFeatureIndex] = featureData;
      await updateDoc(actualUserRef, { features });
    } else {
      // Add new feature
      await updateDoc(actualUserRef, {
        features: arrayUnion(featureData)
      });
    }
    
    // For AI features, also update the subcollection document to ensure compatibility with both systems
    if (featureId === 'ai_features_unlock') {
      const featureSubDoc = doc(db, 'users', userId, 'features', 'ai_features_unlock');
      await setDoc(featureSubDoc, {
        unlocked: true,
        unlockedAt: now.toISOString(),
        expiryDate: expiryDate ? expiryDate.toISOString() : null,
        adminGranted: true
      }, { merge: true });
    }
    
    return { 
      success: true, 
      message: `Feature ${featureId} granted to user ${userData.email || userId}`, 
      expiryDate: expiryDate ? expiryDate.toISOString() : 'Never'
    };
  } catch (error) {
    console.warn('Error granting feature to user:', error);
    return { success: false, error: error.message };
  }
}

// Remove a feature from a user (admin function)
export async function removeFeatureFromUser(userId, featureId) {
  try {
    if (!userId || !featureId) {
      throw new Error('User ID and feature ID are required');
    }
    
    // Try to find user by userId first
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      // If user is not found by ID, try to find by email
      // This allows admins to enter an email instead of a Firebase UID
      if (userId.includes('@')) {
        const usersRef = collection(db, 'users');
        const q = query(usersRef, where('email', '==', userId));
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          return { success: false, error: 'User not found with the provided email' };
        }
        
        // Use the first user found with this email
        const foundUserDoc = querySnapshot.docs[0];
        userId = foundUserDoc.id;
      } else {
        return { success: false, error: 'User not found' };
      }
    }
    
    // Re-fetch user document if we found it by email
    const actualUserRef = doc(db, 'users', userId);
    const actualUserDoc = await getDoc(actualUserRef);
    const userData = actualUserDoc.data();
    const features = userData.features || [];
    
    const updatedFeatures = features.filter(f => f.id !== featureId);
    
    // Update the user document
    await updateDoc(actualUserRef, {
      features: updatedFeatures
    });
    
    // For AI features, also update or delete the feature document in the subcollection
    if (featureId === 'ai_features_unlock') {
      const featureSubDoc = doc(db, 'users', userId, 'features', 'ai_features_unlock');
      const featureDocSnapshot = await getDoc(featureSubDoc);
      
      if (featureDocSnapshot.exists()) {
        // Mark as expired by setting expiry date to yesterday
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        await updateDoc(featureSubDoc, {
          unlocked: false,
          expiryDate: yesterday.toISOString(),
          removedAt: new Date().toISOString()
        });
      }
    }
    
    return { 
      success: true, 
      message: `Feature ${featureId} removed from user ${userData.email || userId}`
    };
  } catch (error) {
    console.warn('Error removing feature from user:', error);
    return { success: false, error: error.message };
  }
}